import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, Button, Col, DatePicker, Form, Input, Modal, Row, Spin, Typography, Upload, message, notification } from 'antd';
import config from '../../../config';
import {
  backButton,
  handleBackendFormErrors,
  tailFormItemLayout,
  unexpectedError,
  getBase64,
  formatDate,
  parseDate,
  formItemLayout,
  beforeUpload,
} from '../../../common_functions';
import UploadTrigger from '../../../common_functions/UploadTrigger';

const MountTypeChar = props => {
  const { section, id } = useParams();
  const [loading, setLoading] = useState(false);
  const [mountTypesChar, setMountTypesChar] = useState({});
  const formRef = useRef(null);

  // Preview Image
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(false);
  const [previewTitle, setPreviewTitle] = useState(false);
  const [images, setImage] = useState(null);
  const [imgID, setImgID] = useState(null);
  const forDateTimeFields = (data, formSubmit = false) => {
    const fieldsToProcess = ['estimated_period_begin', 'estimated_period_end', 'secondary_period_begin', 'secondary_period_end'];
    for (const field of fieldsToProcess) {
      if (data[field]) {
        data[field] = formSubmit ? formatDate(data[field]) : parseDate(data[field]);
      } else {
        data[field] = null;
      }
    }
    return data;
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        if (id !== 'new') {
          let { data } = await props.getItem('mount_types_char', id, {
            fields: ['*,image.id,image.filename_download'],
          });
          if (data?.image?.id) {
            const fileData = await props.getFile(data.image.id);
            setImage([
              {
                uid: data.image.id,
                name: data.image.filename_download,
                url: fileData,
              },
            ]);
            setImgID(data.image.id);
          }
          setMountTypesChar(forDateTimeFields(data));
          formRef.current.setFieldsValue(forDateTimeFields(data));
        }
      } catch (error) {
        unexpectedError(error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [section, id]);
  let onFinishFailed = ({ errorFields }) => {
    formRef.current.scrollToField(errorFields[0].name);
  };

  let onFinish = async values => {
    try {
      setLoading(true);
      if (values?.image?.[0]?.originFileObj) {
        const formData = new FormData();
        formData.append('folder', process.env.mount_images_folder);
        formData.append('file', values.image[0].originFileObj);
        const {
          data: { id: fileID },
        } = await props.saveFile(formData, mountTypesChar?.image?.id);
        values.image = fileID;
      }
      let newState = { ...mountTypesChar, ...values, ...forDateTimeFields(values, true) };

      const response = await props.saveItem('mount_types_char', newState);
      if (response) {
        notification['success']({
          message: 'Mount type saved successfully!',
          duration: 2,
        });
        backButton(props, `/settings/mount-types`);
      }
    } catch (errorResponse) {
      handleBackendFormErrors(formRef, errorResponse);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => setPreviewVisible(false);

  const uploadFile = async ({ file, fileList }) => {
    try {
      setLoading(true);
      if (!(file instanceof File)) return;
      const url = await getBase64(fileList[0].originFileObj);
      setImage([{ uid: '-1', name: file.name, status: 'done', url: url }]);
    } catch (error) {
      console.error(error);
      unexpectedError(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async file => {
    setPreviewImage(file.url);
    setPreviewVisible(true);
    setPreviewTitle(file.name);
  };

  // Custom validation function for period start date year
  const validateStartDate = (_, value) => {
    if (!value) {
      return Promise.reject();
    }
    // Get the period end date year value from the form
    const endDateYear = formRef.current.getFieldValue('estimated_period_end');
    if (endDateYear && value.isAfter(endDateYear)) {
      return Promise.reject(new Error('Period start date must be on or before the end date'));
    }
    formRef.current.setFields([
      {
        name: 'estimated_period_end',
        errors: '',
      },
    ]);
    return Promise.resolve();
  };

  // Custom validation function for period end date year
  const validateEndDate = (_, value) => {
    if (!value) {
      return Promise.reject();
    }
    // Get the period start date year value from the form
    const startDateYear = formRef.current.getFieldValue('estimated_period_begin');
    if (startDateYear && value.isBefore(startDateYear)) {
      return Promise.reject(new Error('Period end date must be on or after the start date!'));
    }
    formRef.current.setFields([
      {
        name: 'estimated_period_begin',
        errors: '',
      },
    ]);
    return Promise.resolve();
  };

  // Custom validation function for secondary period begin
  const validateSecondaryStartDate = (_, value) => {
    // Get the period end date year value from the form
    const endDateYear = formRef.current.getFieldValue('secondary_period_end');
    if (endDateYear && value.isAfter(endDateYear)) {
      return Promise.reject(new Error('Secondary period start date must be on or before the end date!'));
    }
    formRef.current.setFields([
      {
        name: 'secondary_period_end',
        errors: '',
      },
    ]);
    return Promise.resolve();
  };

  // Custom validation function for secondary period end
  const validateSecondaryEndDate = (_, value) => {
    // Get the period start date year value from the form
    const startDateYear = formRef.current.getFieldValue('secondary_period_begin');
    if (startDateYear && value.isBefore(startDateYear)) {
      return Promise.reject(new Error('Secondary period end date must be on or after the start date!'));
    }
    formRef.current.setFields([
      {
        name: 'secondary_period_begin',
        errors: '',
      },
    ]);
    return Promise.resolve();
  };

  const normFile = e => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  return (
    <Spin spinning={loading} size="large">
      <Row>
        <Col className="app-breadcrumb-col">
          <Breadcrumb items={[{ title: "Settings", href: "/settings/mount-types" }, { title: "Mount Types", href: "/settings/mount-types" }, { title: id != 'new' ? `Edit Mount Type` : 'Add Mount Type' }]} />
        </Col>
        <Col className="white-paper">
          <Row gutter={24} className="page-header-section" align="middle">
            <Col xs={12}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {id != 'new' ? `Edit Mount Type: ${id}` : 'Add Mount Type'}
              </Typography.Title>
            </Col>
            <Col xs={12} justify="end"></Col>
          </Row>
          <hr />
          <div className="artist">
            <Form
              ref={formRef}
              layout="vertical"
              {...formItemLayout}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              initialValues={{ layout: tailFormItemLayout }}
            >
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="brand_name"
                    label="Brand Name"
                    rules={[
                      {
                        whitespace: true,
                        required: false,
                        message: 'Brand name is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter brand name" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="model"
                    label="Model"
                    rules={[
                      {
                        whitespace: true,
                        required: false,
                        message: 'Model is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter model" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="estimated_period_begin"
                    label="Estimated Period Begin"
                    rules={[
                      {
                        required: true,
                        message: 'Estimated period begin is required!',
                      },
                      {
                        validator: validateStartDate, // Custom validation function
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter year" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="estimated_period_end"
                    label="Estimated Period End"
                    rules={[
                      {
                        required: true,
                        message: 'Estimated period end date is required!',
                      },
                      {
                        validator: validateEndDate, // Custom validation function
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter year" />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={24}>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="secondary_period_begin"
                    label="Secondary Period Begin"
                    rules={[
                      {
                        required: false,
                        // message: 'Secondary period begin date is required!',
                      },
                      {
                        validator: validateSecondaryStartDate, // Custom validation function
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter year" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="secondary_period_end"
                    label="Secondary Period End"
                    rules={[
                      {
                        required: false,
                        message: 'Secondary period end date is required!',
                      },
                      {
                        validator: validateSecondaryEndDate, // Custom validation function
                      },
                    ]}
                  >
                    <DatePicker picker="year" placeholder="Enter year" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="type"
                    label="Type"
                    rules={[
                      {
                        whitespace: true,
                        required: true,
                        message: 'Type is required!',
                      },
                    ]}
                  >
                    <Input placeholder="Enter type" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="note"
                    label="Note"
                    rules={[
                      {
                        whitespace: true,
                        required: false,
                      },
                    ]}
                  >
                    <Input.TextArea placeholder="Enter note" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={12} lg={12}>
                  <Form.Item
                    name="image"
                    label="Image"
                    getValueFromEvent={normFile}
                    rules={[
                      {
                        required: false,
                        message: 'Image is required!',
                      },
                    ]}
                  >
                    <Upload
                      name="file"
                      listType="picture"
                      className="create_object"
                      accept={config.FileType.imageType.toString()}
                      beforeUpload={beforeUpload}
                      showUploadList={{
                        showRemoveIcon: mountTypesChar?.image ? false : true,
                        showRemoveIcon: false,
                      }}
                      onPreview={handlePreview}
                      maxCount={1}
                      multiple={false}
                      onChange={uploadFile}
                      fileList={images}
                    >
                      <UploadTrigger fileType="imageType" />
                    </Upload>
                  </Form.Item>
                  <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel}>
                    <div style={{ textAlign: 'center' }}>
                      <img
                        alt="No Image"
                        style={{
                          width: '100%',
                          height: 'auto',
                          textAlign: 'center',
                          cursor: 'pointer',
                        }}
                        src={previewImage}
                      />
                    </div>
                  </Modal>
                </Col>
              </Row>
              <Form.Item {...tailFormItemLayout}>
                <Button
                  type="default"
                  htmlType="button"
                  onClick={() => backButton(props, '/settings/mount-types')}
                  style={{
                    borderColor: '#d9d9d9',
                    background: '#f5f5f5',
                    color: 'rgba(0, 0, 0, 0.85)',
                  }}
                >
                  Back
                </Button>
                {props.showButton(
                  props.roleType,
                  props.tabs_permissions.settings_mount_types_char,
                  !!mountTypesChar ? mountTypesChar.id : ''
                )}
              </Form.Item>
            </Form>
          </div>
        </Col>
      </Row>
    </Spin>
  );
};

export default MountTypeChar;
